<template>
  <div class="header">
    <div class="container-fluid">
      <div class="header-body">
        <div class="row align-items-center">
          <div class="col">
            <h6 class="header-pretitle">Settings</h6>
            <h1 class="header-title">User Role Management</h1>
          </div>
        </div>
      </div>

      <div class="card mt-4">
        <div class="card-header">
          <h4 class="card-header-title">Users</h4>
        </div>

        <div class="card-body">
          <div class="row mb-4">
            <div class="col-md-4 mb-3">
              <label class="form-label">Search</label>
              <input
                v-model="filters.search"
                type="text"
                class="form-control"
                placeholder="Search NIK, iSafe, name, email, or company"
                @input="onFilterChange"
              />
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">Company</label>
              <input
                v-model="filters.company"
                type="text"
                class="form-control"
                placeholder="All Companies"
                @input="onFilterChange"
              />
            </div>
            <div class="col-md-3 mb-3">
              <label class="form-label">Role</label>
              <v-select
                v-model="filters.role_id"
                :options="roleOptions"
                :reduce="option => option.id"
                label="name"
                placeholder="All Roles"
                :clearable="true"
                @input="onFilterChange"
              />
            </div>
            <div class="col-md-2 mb-3 d-flex align-items-end justify-content-end">
              <button type="button" class="btn btn-primary" @click="clearFilters">
                <i class="fe fe-x mr-1"></i>
                Clear
              </button>
            </div>
          </div>

          <div v-if="isLoading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading users...</p>
          </div>

          <vue-good-table
            v-else
            mode="remote"
            :columns="columns"
            :rows="rows"
            :totalRows="totalItems"
            :sort-options="{ enabled: false }"
            :pagination-options="{
              enabled: true,
              mode: 'pages',
              position: 'bottom',
              perPage: params.limit,
              perPageDropdown: [10, 25, 50, 100],
              dropdownAllowAll: false,
              nextLabel: 'Next',
              prevLabel: 'Prev',
              rowsPerPageLabel: 'Rows per page',
              ofLabel: 'of',
              pageLabel: 'page',
              allLabel: 'All'
            }"
            style-class="vgt-table border-0 table"
            @on-page-change="onPageChange"
            @on-per-page-change="onPerPageChange">
            <div slot="emptystate" class="text-center py-5">
              No users found
            </div>

            <template slot="table-row" slot-scope="props">
              <span v-if="props.column.field === 'no'">
                {{ (params.page - 1) * params.limit + props.index + 1 }}
              </span>

              <span v-else-if="props.column.field === 'user'">
                <strong>{{ props.row.fullname || '-' }}</strong>
                <div class="text-muted small">{{ props.row.email || '-' }}</div>
              </span>

              <span v-else-if="props.column.field === 'identity'">
                <span class="badge badge-primary mr-1">{{ props.row.nik || '-' }}</span>
                <span class="badge badge-light">{{ props.row.isafe_no || '-' }}</span>
              </span>

              <span v-else-if="props.column.field === 'job'">
                {{ props.row.position || '-' }}
                <div class="text-muted small">{{ props.row.department || '-' }}</div>
              </span>

              <span v-else-if="props.column.field === 'current_role'">
                <span class="badge badge-info">{{ getRoleName(props.row.role_id) }}</span>
              </span>

              <span v-else-if="props.column.field === 'role_update'">
                <v-select
                  v-model="selectedRoles[props.row.id]"
                  :options="roleOptions"
                  :reduce="option => option.id"
                  label="name"
                  :clearable="false"
                  class="role-select"
                />
              </span>

              <span v-else-if="props.column.field === 'action'">
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  :disabled="isSaving || !canSave(props.row)"
                  @click="saveRole(props.row)">
                  <i class="fe fe-save"></i>
                </button>
              </span>

              <span v-else>
                {{ props.formattedRow[props.column.field] || '-' }}
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "UserRoleManagement",
  data() {
    return {
      filterTimer: null,
      filters: {
        search: "",
        company: "",
        role_id: null,
      },
      params: {
        page: 1,
        limit: 10,
      },
      selectedRoles: {},
      columns: [
        { label: "No", field: "no", width: "70px", sortable: false },
        { label: "User", field: "user", sortable: false },
        { label: "NIK / iSafe", field: "identity", width: "170px", sortable: false },
        { label: "Company", field: "company", sortable: false },
        { label: "Position", field: "job", sortable: false },
        { label: "Current Role", field: "current_role", width: "160px", sortable: false },
        { label: "Set Role", field: "role_update", width: "240px", sortable: false },
        { label: "Action", field: "action", width: "90px", tdClass: "text-center", sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters("userRoleManagement", {
      users: "users",
      roles: "roles",
      isLoading: "isLoading",
      isSaving: "isSaving",
      totalItems: "totalItems",
    }),
    rows() {
      return this.users || [];
    },
    roleOptions() {
      return (this.roles || []).map((role) => ({
        id: role.id,
        name: role.name || role.id,
      }));
    },
  },
  watch: {
    users: {
      deep: true,
      immediate: true,
      handler(users) {
        const selectedRoles = {};
        (users || []).forEach((user) => {
          selectedRoles[user.id] = user.role_id || "employee";
        });
        this.selectedRoles = selectedRoles;
      },
    },
  },
  methods: {
    ...mapActions("userRoleManagement", ["getUsers", "getRoles", "updateUserRole"]),

    async fetchUsers() {
      await this.getUsers({
        ...this.params,
        ...this.filters,
      });
    },

    async fetchInitialData() {
      try {
        await this.getRoles();
        await this.fetchUsers();
      } catch (error) {
        this.showError(error, "Data role user belum berhasil dimuat");
      }
    },

    onFilterChange() {
      window.clearTimeout(this.filterTimer);
      this.filterTimer = window.setTimeout(() => {
        this.params.page = 1;
        this.fetchUsers();
      }, 350);
    },

    onPageChange(params) {
      this.params.page = params.currentPage;
      this.fetchUsers();
    },

    onPerPageChange(params) {
      this.params.limit = params.currentPerPage;
      this.params.page = 1;
      this.fetchUsers();
    },

    clearFilters() {
      this.filters = {
        search: "",
        company: "",
        role_id: null,
      };
      this.params.page = 1;
      this.fetchUsers();
    },

    getRoleName(roleId) {
      const role = this.roleOptions.find((item) => item.id === roleId);
      return role ? role.name : roleId || "-";
    },

    canSave(row) {
      return this.selectedRoles[row.id] && this.selectedRoles[row.id] !== row.role_id;
    },

    async saveRole(row) {
      try {
        await this.updateUserRole({
          id: row.id,
          role_id: this.selectedRoles[row.id],
        });
        this.$swal("Success", "Role user berhasil diperbarui", "success");
        await this.fetchUsers();
      } catch (error) {
        this.showError(error, "User belum berhasil diperbarui role");
      }
    },

    showError(error, fallbackMessage) {
      const message = error?.response?.data?.error || error?.response?.data?.message || error?.message || fallbackMessage;
      this.$swal("Error", message, "error");
    },
  },
  mounted() {
    this.fetchInitialData();
  },
  beforeDestroy() {
    window.clearTimeout(this.filterTimer);
  },
};
</script>

<style scoped>
.role-select {
  min-width: 220px;
}
</style>
